import React, { useEffect } from 'react';
import I18n from '@/modules/i18n';
import Icon from '@/components/ui/Icon';
import Text from '@/components/ui/Text';
import { COLORS } from '@/components/ui/ui.constants';
import cn from '@/utils/cn';
import useAIQuestionBuilderStore from './useAIQuestionBuilderStore';

const ModeSelectPanel = () => {
  const {
    closePanel,
    openConfigurePanel,
    setCreationMode,
  } = useAIQuestionBuilderStore();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closePanel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [closePanel]);

  const handleModeSelect = (mode: 'create' | 'modify') => {
    setCreationMode(mode);
    openConfigurePanel();
  };

  return (
    <div
      className={cn(
        'tw-fixed tw-top-0 tw-right-0 tw-w-[38.75rem] tw-h-full tw-bg-white tw-z-[1050]',
        'tw-flex tw-flex-col tw-border-l-2 tw-border-grey',
        'tw-shadow-lg',
      )}
    >
      <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3">
        <Text variant="heading2" weight="bold">
          {I18n.t('ai_qb_panel_title')}
        </Text>
        <button
          aria-label="Close panel"
          className={cn(
            'tw-text-dark-grey tw-bg-transparent tw-border-none',
            'tw-cursor-pointer tw-w-6 tw-h-6 tw-flex tw-items-center',
            'tw-justify-center hover:tw-text-tci-black',
          )}
          type="button"
          onClick={closePanel}
        >
          <Icon icon="Close" size={16} />
        </button>
      </div>

      <hr className="tw-border-t tw-border-grey tw-m-0" />

      <div className="tw-flex tw-flex-col tw-gap-4 tw-px-5 tw-pt-6 tw-items-center">
        <div className="tw-flex tw-flex-col tw-justify-center tw-items-center">
          <Icon fill={COLORS['dark-grey']} icon="Sparkle" size={60} />
          <Text size="xl" tag="span" variant="heading2">
            {I18n.t('ai_qb_mode_select_title')}
          </Text>
          <Text className="tw-px-16 tw-py-4 !tw-text-[#777770] tw-text-center" size="lg" tag="span" variant="body">
            {I18n.t('ai_qb_mode_select_description')}
          </Text>
        </div>

        <button
          className={cn(
            'tw-flex tw-flex-col tw-items-center tw-gap-2 tw-p-4 tw-border tw-border-solid tw-border-grey',
            'tw-rounded-lg tw-bg-white tw-cursor-pointer tw-text-left tw-transition-colors tw-w-96 !tw-font-tci',
            'tw-relative',
            'hover:tw-border-tci-purple hover:tw-bg-[#f9f7fc]',
          )}
          type="button"
          onClick={() => handleModeSelect('create')}
        >
          <div className="tw-flex tw-items-center tw-justify-center tw-w-full">
            <Icon className="tw-absolute tw-top-2 tw-left-2" fill={COLORS['dark-grey']} icon="Add" size={40} />
            <Text className="tw-m-auto" size="lg" tag="span" weight="bold">
              {I18n.t('ai_qb_mode_create_title')}
            </Text>
          </div>
          <Text className="!tw-text-[#777770] tw-px-14" size="sm" tag="span">
            {I18n.t('ai_qb_mode_create_description')}
          </Text>
        </button>

        <button
          className={cn(
            'tw-flex tw-flex-col tw-items-center tw-gap-2 tw-p-4 tw-border tw-border-solid tw-border-grey',
            'tw-rounded-lg tw-bg-white tw-cursor-pointer tw-text-left tw-transition-colors tw-w-96 !tw-font-tci',
            'tw-relative',
            'hover:tw-border-tci-purple hover:tw-bg-[#f9f7fc]',
          )}
          type="button"
          onClick={() => handleModeSelect('modify')}
        >
          <div className="tw-flex tw-items-center tw-justify-center tw-w-full">
            <Icon className="tw-absolute tw-top-2 tw-left-2" fill={COLORS['dark-grey']} icon="Restart" size={40} />
            <Text className="tw-m-auto" size="lg" tag="span" weight="bold">
              {I18n.t('ai_qb_mode_modify_title')}
            </Text>
          </div>
          <Text className="!tw-text-[#777770] tw-px-14" size="sm" tag="span">
            {I18n.t('ai_qb_mode_modify_description')}
          </Text>
        </button>
      </div>
    </div>
  );
};

export default ModeSelectPanel;
